import { useTranslation } from "react-i18next";
import { Box, Group, Paper, Stack, Text } from "@mantine/core";
import { CampaignProductResults } from "src/graphql/generated/graphql";
import { CampaignQueryDataType } from "src/hooks/campaigns";

import { ReportSection } from "../components/ReportSection";
import { EshopBar } from "../derive";

// Distinct, intentional colours for the e-shop destination bars (cycled). Chosen to sit
// apart from the orange/blue/green of the funnel's first three steps.
const ESHOP_GRADIENTS = [
    "linear-gradient(90deg, #7048E8, #9775FA)", // violet
    "linear-gradient(90deg, #0C8599, #22B8CF)", // cyan
    "linear-gradient(90deg, #9C36B5, #CC5DE8)", // grape
    "linear-gradient(90deg, #C2255C, #F06595)", // rose
    "linear-gradient(90deg, #1864AB, #4DABF7)", // deep blue
];

interface FunnelBarProps {
    label: string;
    value: number;
    gradient: string;
    // Bar width as a % of its group's scale (see ReportFunnel: the acquisition steps scale to
    // total applications, the e-shop bars scale to the review pool so they read fully).
    widthPct: number;
}

const FunnelBar = ({ label, value, gradient, widthPct }: FunnelBarProps) => (
    <Group spacing={16} noWrap align="center">
        {/* Label sits outside the bar so a 0 value reads as an empty track, not a
            misleading min-width coloured bar. */}
        <Text size="sm" weight={600} color="#0F1B3D" style={{ width: 240, flex: "none" }} lineClamp={2}>
            {label}
        </Text>
        <Box style={{ flex: 1, height: 28, background: "#EEF1F6", borderRadius: 8 }}>
            <Box style={{ width: `${widthPct}%`, background: gradient, borderRadius: 8, height: 28 }} />
        </Box>
        <Text
            weight={700}
            color="#0F1B3D"
            style={{ minWidth: 64, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
        >
            {value.toLocaleString("cs-CZ")}
        </Text>
    </Group>
);

export interface ReportFunnelProps {
    campaign: CampaignQueryDataType;
    // Product results (stored or recomputed) — the "Reviews on Testuj.to" step sums approvedCount.
    results?: CampaignProductResults[];
    // Per-e-shop-destination review counts (live; isShipped for integrated, checkbox for custom).
    eshopBars?: EshopBar[];
}

// Review distribution across destinations: reviews on Testuj.to plus one bar per e-shop, all
// scaled to the review pool so they read fully. (Applications / selected / target are shown as
// cards in the "what we tested" section, not here.) Testuj.to keeps its green so it reads as the
// platform rather than an e-shop.
export const ReportFunnel = ({ campaign, results, eshopBars }: ReportFunnelProps) => {
    const { t } = useTranslation(["common"]);

    const locale = campaign?.mainTenant?.locale || campaign?.tenant?.locale;

    const totalReviews = (results || []).reduce((sum, product) => sum + (product?.approvedCount || 0), 0);
    const eshopSteps = eshopBars || [];

    const distributionSteps = [
        {
            key: "__tt__",
            label: t("campaign_report_reviews_on_tt"),
            value: totalReviews,
            gradient: "linear-gradient(90deg, #168F5B, #33B277)",
        },
        ...eshopSteps.map((destination, index) => ({
            key: destination.key,
            label: `${t("campaign_report_reviews_on")} ${destination.name}`,
            value: destination.count,
            gradient: ESHOP_GRADIENTS[index % ESHOP_GRADIENTS.length],
        })),
    ];

    // Scale to the review pool (never below the largest bar) so counts read fully.
    const scaleMax = Math.max(...distributionSteps.map((step) => step.value), 1);

    return (
        <ReportSection
            title={t("campaign_report_funnel")}
            meta={
                locale && (
                    <>
                        {t("campaign_report_locale")}{" "}
                        <strong style={{ color: "#0F1B3D" }}>{locale.toUpperCase()}</strong>
                    </>
                )
            }
        >
            <Paper withBorder radius={16} p={24}>
                <Text
                    size="xs"
                    weight={700}
                    color="#7A8699"
                    mb={12}
                    style={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                    {t("campaign_report_eshop_distribution")}
                </Text>
                <Stack spacing={14}>
                    {distributionSteps.map((step) => (
                        <FunnelBar
                            key={step.key}
                            label={step.label}
                            value={step.value}
                            gradient={step.gradient}
                            widthPct={Math.min(100, (step.value / scaleMax) * 100)}
                        />
                    ))}
                </Stack>
            </Paper>
        </ReportSection>
    );
};
